import { parseSizeMmOriented, isHorizontal } from './labelSizeConfig';
import type { LabelMetrics, ParsedField } from './labelPrintTypes';
import { buildSharedElementCss, buildStyleMetrics } from './label-print';

/** Convert a number to a CSS mm string */
export const mm = (val: number): string => `${val}mm`;

/** Escape dynamic text before injecting into generated HTML */
export const escapeHtml = (value: unknown): string => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Render an array of text fields as HTML spans with the given CSS class (empty value → non-breaking space to preserve line height) */
export const renderTextSpans = (fields: ParsedField[], cssClass: string): string =>
    fields.map((f) => `<span class="${cssClass}">${f.value ? escapeHtml(f.value) : '&nbsp;'}</span>`).join('\n                ');

/** Build an inline style string for a constrained QR code cell */
export const buildQrStyle = (qrMm: number): string =>
    `width:${mm(qrMm)};height:${mm(qrMm)};max-width:${mm(qrMm)};max-height:${mm(qrMm)};overflow:hidden;`;

/** Extract SVG elements from an HTML string */
export const extractSvgs = (html: string): string[] =>
    html.match(/<svg[\s\S]*?<\/svg>/gi) ?? [];

/** Compute label metrics (all values in mm) from API size + orientation */
export const computeMetrics = (size?: string, orientation?: string, isMaterialMode = true, applyMaterialZoom = false): LabelMetrics => {
    const parsed = parseSizeMmOriented(size, orientation);
    const horizontal = isHorizontal(orientation);
    // Label dimensions ALWAYS match the template's configured size. Material print
    // only scales the text content (see materialTextScaleFactor); the label canvas
    // and the QR remain at the template's physical size.
    void applyMaterialZoom;
    void isMaterialMode;
    const widthMm = parsed.widthMm;
    const heightMm = parsed.heightMm;
    const isSplotHorizontal = horizontal && !isMaterialMode;
    const isMaterialHorizontal = horizontal && isMaterialMode;
    const isMaterialVertical = !horizontal && isMaterialMode;
    // For material vertical, use the shorter dimension (widthMm) with the horizontal factor
    // so font sizes match horizontal label sizing (same as popup preview).
    const baseTitleFontMm = isMaterialVertical
        ? Math.max(2.5, widthMm * 0.08)
        : Math.max(2.5, heightMm * (horizontal ? 0.08 : 0.04));
    const titleFontMm = isSplotHorizontal
        ? Math.max(baseTitleFontMm * 1.2, heightMm * 0.098)
        : baseTitleFontMm;
    // Keep origin sizing stable so only the title gets boosted for SPLot.
    const originFontMm = Math.max(2, (isSplotHorizontal ? baseTitleFontMm : titleFontMm) * 0.7);
    const footerFontMm = Math.max(1.8, titleFontMm * 0.55);
    // QR derives from the (unchanged) label dimensions and is NOT scaled by material zoom.
    const qrRowMm = heightMm * (isSplotHorizontal ? 0.78 : isMaterialHorizontal ? 0.34 : isMaterialVertical ? 0.36 : 0.5);
    const qrMaxWidthMm = widthMm * (isSplotHorizontal ? 0.42 : isMaterialHorizontal ? 0.24 : isMaterialVertical ? 0.33 : horizontal ? 0.35 : 0.40);
    const qrInsetMm = isSplotHorizontal ? 0.8 : 2;
    const qrBaseMm = Math.min(qrRowMm, qrMaxWidthMm) - qrInsetMm;
    const qrMm = Math.max(6, Math.round(qrBaseMm * 10) / 10);
    return { widthMm, heightMm, titleFontMm, originFontMm, footerFontMm, qrMm };
};

/** Build the full CSS for label printing — guards both horizontal and vertical to the exact API mm size */
export const buildLabelCss = (m: LabelMetrics, horizontal: boolean, extraContainerCss = '', multiLabel = false, isMaterialMode = true, applyMaterialZoom = false): string => {
    const metrics = buildStyleMetrics(m, { horizontal, isMaterialMode, applyMaterialZoom });
    return `
        @page {
            size: ${metrics.w}mm ${metrics.h}mm;
            margin: 0mm;
            padding: 0mm;
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body {
            width: ${metrics.w}mm;
            ${multiLabel ? 'height: auto;' : `height: ${metrics.h}mm;`}
            max-width: ${metrics.w}mm;
            ${multiLabel ? 'max-height: none;' : `max-height: ${metrics.h}mm;`}
            margin: 0; padding: 0;
            ${multiLabel ? 'overflow: visible;' : 'overflow: hidden;'}
            font-family: 'Noto Sans', Arial, sans-serif;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            -webkit-text-size-adjust: 100%;
            text-size-adjust: 100%;
        }
        .label-container {
            display: grid;
            grid-template-rows: ${metrics.gridRows};
            width: ${metrics.w}mm; max-width: ${metrics.w}mm;
            height: ${metrics.h}mm; max-height: ${metrics.h}mm;
            overflow: hidden;
            padding: ${metrics.containerPadding};
            ${extraContainerCss}
            page-break-inside: avoid;
            page-break-after: avoid;
        }
        .label-container:last-child { page-break-after: auto; }
        ${buildSharedElementCss(metrics, horizontal)}
        @media print {
            html, body {
                width: ${metrics.w}mm !important;
                ${multiLabel ? 'height: auto !important;' : `height: ${metrics.h}mm !important;`}
                max-width: ${metrics.w}mm !important;
                ${multiLabel ? 'max-height: none !important;' : `max-height: ${metrics.h}mm !important;`}
                margin: 0 !important; padding: 0 !important;
                ${multiLabel ? 'overflow: visible !important;' : 'overflow: hidden !important;'}
                transform: none !important;
                zoom: 1 !important;
            }
            .label-container {
                width: ${metrics.w}mm !important;
                height: ${metrics.h}mm !important;
                max-width: ${metrics.w}mm !important;
                max-height: ${metrics.h}mm !important;
                overflow: hidden !important;
                page-break-inside: avoid !important;
                page-break-after: avoid !important;
                margin: 0 !important;
                padding: ${metrics.containerPadding} !important;
            }
            .label-container:last-child {
                page-break-after: auto !important;
            }
        }
`;
};

/** Wrap label body HTML in a full print-ready HTML document */
export const wrapInHtmlDocument = (title: string, css: string, body: string): string =>
    `<!DOCTYPE html>
<html>
<head>
    <title>${title}</title>
    <style>${css}</style>
</head>
<body>
    ${body}
</body>
</html>`;

/** Build CSS for row-based multi-label print layout with tight page sizing */
export const buildGridPrintCss = (
    m: LabelMetrics,
    horizontal: boolean,
    gapMm: number,
    pageWidth: number,
    pageHeight: number,
    isMaterialMode = true,
    leftMarginMm = 0,
    applyMaterialZoom = false,
    pageGridColumns = 1,
    pageGridRows = 1,
    is6LabelLayout = false
): string => {
    const metrics = buildStyleMetrics(m, {
        horizontal,
        isMaterialMode,
        applyMaterialZoom,
        is6LabelLayout,
        leftMarginMm,
        lineHeightMultiplier: is6LabelLayout ? 1.3 : 1,
        isForPrint: true,
    });
    
    return `
        * { margin: 0; padding: 0; box-sizing: border-box; }
        @page {
            size: ${pageWidth}mm ${pageHeight}mm;
            margin: 0;
        }
        html, body {
            width: ${pageWidth}mm;
            margin: 0; padding: 0;
            font-family: 'Noto Sans', Arial, sans-serif;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            -webkit-text-size-adjust: 100%;
            text-size-adjust: 100%;
        }
        .print-page {
            display: ${pageGridColumns > 1 ? 'grid' : 'flex'};
            ${pageGridColumns > 1 ? `grid-template-columns: repeat(${pageGridColumns}, 1fr);` : ''}
            ${pageGridColumns > 1 ? `grid-template-rows: repeat(${pageGridRows}, 1fr);` : ''}
            ${pageGridColumns > 1 ? `gap: ${gapMm}mm;` : `flex-direction: column;\n            gap: ${gapMm}mm;`}
            width: ${pageWidth}mm;
            height: ${pageHeight}mm;
            margin: 0;
            padding: 0;
            page-break-after: always;
            page-break-inside: avoid;
        }
        .print-page:last-child {
            page-break-after: auto;
        }
        .label-container {
            display: grid;
            grid-template-rows: ${metrics.gridRows};
            align-items: center;
            justify-items: stretch;
            width: ${metrics.w}mm; max-width: ${metrics.w}mm;
            height: ${metrics.h}mm; max-height: ${metrics.h}mm;
            margin-left: ${metrics.effectiveOuterLeftMarginMm}mm;
            flex: 0 0 auto;
            overflow: hidden;
            padding: ${metrics.containerPadding};
            border: ${metrics.isSplotMode ? 'none' : '0.1mm solid #ccc'};
            ${pageGridColumns > 1 ? 'border: none;' : ''}
        }
        ${buildSharedElementCss(metrics, horizontal)}
        @media print {
            html, body {
                width: ${pageWidth}mm !important;
                margin: 0 !important; padding: 0 !important;
                transform: none !important;
                zoom: 1 !important;
            }
            .print-page {
                width: ${pageWidth}mm !important;
                height: ${pageHeight}mm !important;
                margin: 0 !important;
                padding: 0 !important;
                page-break-after: always;
                page-break-inside: avoid;
                break-after: page;
                break-inside: avoid;
            }
            .print-page:last-child {
                page-break-after: auto;
                break-after: auto;
            }
            .label-container {
                width: ${metrics.w}mm !important;
                height: ${metrics.h}mm !important;
                max-width: ${metrics.w}mm !important;
                max-height: ${metrics.h}mm !important;
                margin-left: ${metrics.effectiveOuterLeftMarginMm}mm !important;
                padding: ${metrics.containerPadding} !important;
                align-items: center !important;
                justify-items: stretch !important;
                flex: 0 0 auto !important;
                overflow: hidden !important;
                border: ${metrics.isSplotMode ? 'none' : '0.1mm solid #ccc'} !important;
            }
            @page {
                size: ${pageWidth}mm ${pageHeight}mm;
                margin: 0;
            }
        }
`;
};
